import React, { useState } from 'react';
import { createWhyUs, updateWhyUs } from '../../services/api';
import { FormInput } from '../components/FormInput';
import { useImageUpload } from '../hooks/useImageUpload';
import { textToArray, arrayToText, getFullImageUrl } from '../utils/helpers';
import toast from 'react-hot-toast';

const WhyUsForm = ({ editingItem, onClose, onRefresh, whyUsCategories = [] }) => {
  const [formData, setFormData] = useState({
    title: editingItem?.title || '',
    category: editingItem?.categoryName || editingItem?.category || 'General',
    icon: editingItem?.icon || 'fas fa-star',
    description: editingItem?.description || '',
    points: arrayToText(editingItem?.points || []),
    imageUrl: editingItem?.imageUrl || '',
    order: editingItem?.order || 0,
    isActive: editingItem?.isActive !== false
  });
  const [loading, setLoading] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const { imageFile, uploading, preview, handleImageChange, uploadImage } = useImageUpload();

  const iconOptions = [
    { value: 'fas fa-star', label: '⭐ Star' },
    { value: 'fas fa-rocket', label: '🚀 Rocket' },
    { value: 'fas fa-cogs', label: '⚙️ Cogs' },
    { value: 'fas fa-shield-alt', label: '🛡️ Shield' },
    { value: 'fas fa-trophy', label: '🏆 Trophy' },
    { value: 'fas fa-chart-line', label: '📈 Chart' },
    { value: 'fas fa-users', label: '👥 Users' },
    { value: 'fas fa-globe', label: '🌍 Globe' },
    { value: 'fas fa-lightbulb', label: '💡 Lightbulb' },
    { value: 'fas fa-heart', label: '❤️ Heart' },
    { value: 'fas fa-handshake', label: '🤝 Handshake' },
    { value: 'fas fa-clock', label: '🕒 Clock' }
  ];

  const getCategoryIcon = (categoryName) => {
    const category = whyUsCategories.find(c => c.name === categoryName);
    return category?.icon || '⭐';
  };

  const handleAddNewCategory = async () => {
    if (!newCategory.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://inteliheads.in/api'}/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          name: newCategory.trim(),
          type: 'whyus',
          order: whyUsCategories.length + 1,
          isActive: true,
          icon: '⭐'
        })
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success('Category added successfully');
        onRefresh();
        setFormData({ ...formData, category: newCategory.trim() });
        setNewCategory('');
        setShowNewCategoryInput(false);
      } else {
        toast.error(result.message || 'Failed to add category');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Error adding category');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Use 'General' as default category if none selected
    const categoryValue = formData.category || 'General';
    
    setLoading(true);
    let imageUrl = formData.imageUrl;
    if (imageFile) imageUrl = await uploadImage();
    
    const submitData = {
      title: formData.title,
      category: categoryValue,  // Send as string
      icon: formData.icon,
      description: formData.description,
      points: textToArray(formData.points),
      imageUrl: imageUrl,
      order: parseInt(formData.order) || 0,
      isActive: formData.isActive
    };
    
    try {
      if (editingItem && editingItem._id) {
        await updateWhyUs(editingItem._id, submitData);
        toast.success('Why Us item updated successfully');
      } else {
        await createWhyUs(submitData);
        toast.success('Why Us item created successfully');
      }
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error saving why us item:', error);
      toast.error(error.message || 'Error saving why us item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '650px', width: '90%', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#0A192F', margin: 0 }}>{editingItem ? 'Edit Why Us Item' : 'Add New Why Us Item'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          {/* Category Selection */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Category <span style={{ color: '#DC2626' }}>*</span>
            </label>
            
            {!showNewCategoryInput ? (
              <>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px', marginBottom: '0.5rem' }}
                >
                  <option value="General">⭐ General</option>
                  {whyUsCategories.map((cat) => (
                    <option key={cat._id} value={cat.name}>
                      {cat.icon} {cat.name}
                    </option>
                  ))}
                </select>
                
                <button 
                  type="button"
                  onClick={() => setShowNewCategoryInput(true)}
                  style={{
                    padding: '0.4rem 0.8rem',
                    background: 'transparent',
                    color: '#2C7A7B',
                    border: '1px solid #2C7A7B',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem'
                  }}
                >
                  <i className="fas fa-plus"></i> Add New Category
                </button>
              </>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Enter new category name"
                    style={{ flex: 1, padding: '0.5rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleAddNewCategory}
                    style={{ padding: '0.5rem 1rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewCategoryInput(false);
                      setNewCategory('');
                    }}
                    style={{ padding: '0.5rem 1rem', background: '#5A6E7F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                </div>
                <small style={{ color: '#5A6E7F' }}>New category will be added to the list</small>
              </div>
            )}
            
            {formData.category && (
              <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#E6F7F5', borderRadius: '8px' }}>
                <small style={{ color: '#2C7A7B' }}>
                  <i className="fas fa-info-circle"></i> Selected: {getCategoryIcon(formData.category)} {formData.category}
                </small>
              </div>
            )}
          </div>
          
          {/* Title */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Title <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input 
              type="text" 
              required 
              value={formData.title} 
              onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
              placeholder="e.g., Expert Team, Global Presence, Innovative Solutions"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Icon Selection */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Icon</label>
            <select
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
            >
              {iconOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Description */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Description</label>
            <textarea 
              value={formData.description} 
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
              rows="3" 
              placeholder="Brief description of this why us item"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Key Points */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Key Points (one per line)</label>
            <textarea 
              value={formData.points} 
              onChange={(e) => setFormData({ ...formData, points: e.target.value })} 
              rows="5" 
              placeholder="Expert team with 30+ years experience&#10;Global presence in 6 continents&#10;Proven track record with 500+ clients&#10;24/7 customer support"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
            <small style={{ color: '#5A6E7F' }}>Each point will be displayed with a checkmark icon</small>
          </div>
          
          {/* Order and Active Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Display Order</label>
              <input 
                type="number" 
                value={formData.order} 
                onChange={(e) => setFormData({ ...formData, order: e.target.value })} 
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
              <small style={{ color: '#5A6E7F' }}>Lower numbers appear first</small>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={formData.isActive} 
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} 
                />
                <span>Active</span>
              </label>
            </div>
          </div>
          
          {/* Image Upload */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Image (Optional)</label>
            
            {(preview || formData.imageUrl) && (
              <div style={{ marginBottom: '0.5rem' }}>
                <img 
                  src={preview || getFullImageUrl(formData.imageUrl)} 
                  alt="Preview" 
                  style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} 
                />
              </div>
            )}
            
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{
                display: 'inline-block',
                padding: '0.4rem 0.8rem',
                background: '#2C7A7B',
                color: 'white',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}>
                <i className="fas fa-upload"></i> Choose Image
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={(e) => handleImageChange(e.target.files[0])} 
                  style={{ display: 'none' }} 
                />
              </label>
              <small style={{ color: '#5A6E7F', marginLeft: '0.5rem' }}>Max 5MB, JPG, PNG, GIF, WEBP</small>
            </div>
            
            <input 
              type="text" 
              value={formData.imageUrl} 
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })} 
              placeholder="Or enter image URL directly" 
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Submit Buttons */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button 
              type="submit" 
              disabled={loading || uploading} 
              style={{ 
                flex: 1, 
                padding: '0.75rem', 
                background: '#2C7A7B', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer', 
                fontWeight: 600,
                opacity: (loading || uploading) ? 0.7 : 1
              }}
            >
              {loading || uploading ? (
                <><i className="fas fa-spinner fa-spin"></i> Saving...</>
              ) : (
                <><i className="fas fa-save"></i> Save Why Us Item</>
              )}
            </button>
            <button 
              type="button" 
              onClick={onClose} 
              style={{ 
                flex: 1, 
                padding: '0.75rem', 
                background: '#5A6E7F', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer', 
                fontWeight: 600 
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WhyUsForm;